import { jsx as _jsx } from "react/jsx-runtime";
import { useOrgAdminT } from "../i18n/context.js";
/** Translate a badge value via `badges.<group>.<value>`, falling back to the
 *  raw value for anything not in the dictionary (t() returns the key path on
 *  a miss, so compare against it). Keeps unknown enum values rendering. */
function badgeLabel(t, group, value) {
    const key = `badges.${group}.${value}`;
    const label = t(key);
    return label === key ? value : label;
}
const STATUS_COLORS = {
    ACTIVE: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    SUSPENDED: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    PROVISIONING: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    // DELETED is the reversible soft-delete (rose distinguishes it from
    // TERMINATED's grey-finality + SUSPENDED's yellow / DEACTIVATING's
    // orange — operators should read it as "removed, but recoverable").
    DELETED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    TERMINATED: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
    DEACTIVATED: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
    DEACTIVATING: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
};
const TIER_COLORS = {
    free: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    starter: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    professional: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
    enterprise: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};
const ROLE_COLORS = {
    ORG_ADMIN: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    MEMBER: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};
const DEFAULT_COLOR = "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
const BASE = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
export function StatusBadge({ status }) {
    const t = useOrgAdminT();
    return _jsx("span", { className: `${BASE} ${STATUS_COLORS[status] ?? DEFAULT_COLOR}`, children: badgeLabel(t, "status", status) });
}
export function TierBadge({ tier }) {
    const t = useOrgAdminT();
    return _jsx("span", { className: `${BASE} ${TIER_COLORS[tier] ?? DEFAULT_COLOR}`, children: badgeLabel(t, "tier", tier) });
}
export function RoleBadge({ role }) {
    const t = useOrgAdminT();
    return _jsx("span", { className: `${BASE} ${ROLE_COLORS[role] ?? DEFAULT_COLOR}`, children: badgeLabel(t, "role", role) });
}
// ── Storage-mode badge (S5 of the shared-storage plan) ──────────────
//
// Reflects `org.settings.storageMode`. "shared" is the lowest-cost
// multi-tenant pool (neutral slate); "dedicated" is the per-org Premium
// NFS share (warmer amber to signal the higher per-org cost line).
// READ-ONLY — the field is immutable post-provision (UpdateOrgSchema
// rejects it), so this badge is never paired with an edit affordance.
const STORAGE_MODE_COLORS = {
    shared: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    dedicated: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};
export function StorageModeBadge({ mode }) {
    const t = useOrgAdminT();
    const titleKey = `badges.storageModeTitle.${mode}`;
    const title = t(titleKey);
    return (_jsx("span", { title: title === titleKey ? t("badges.storageTitleFallback", { mode }) : title, className: `${BASE} ${STORAGE_MODE_COLORS[mode] ?? DEFAULT_COLOR}`, "data-testid": `storage-mode-badge-${mode}`, children: badgeLabel(t, "storageMode", mode) }));
}
// ── Phase D broadening badges ────────────────────────────────────────
const CLIENT_STATUS_COLORS = {
    active: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    prospect: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    inactive: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
    former: "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500",
};
const MATTER_STATUS_COLORS = {
    active: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    paused: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    closed: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};
const MATTER_TYPE_COLORS = {
    // Contencioso branches (warm palette)
    civil: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    labor: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    tax: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    // Non-contentious (cool palette)
    contract: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
    advisory: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
    corporate: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    realestate: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
    compliance: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    other: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};
const ASSIGNMENT_ROLE_COLORS = {
    LEAD: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    HANDLER: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    CONTRIBUTOR: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
    OBSERVER: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};
export function ClientStatusBadge({ status }) {
    const t = useOrgAdminT();
    return _jsx("span", { className: `${BASE} ${CLIENT_STATUS_COLORS[status] ?? DEFAULT_COLOR}`, children: badgeLabel(t, "clientStatus", status) });
}
export function MatterStatusBadge({ status }) {
    const t = useOrgAdminT();
    return _jsx("span", { className: `${BASE} ${MATTER_STATUS_COLORS[status] ?? DEFAULT_COLOR}`, children: badgeLabel(t, "matterStatus", status) });
}
export function MatterTypeBadge({ matterType }) {
    const t = useOrgAdminT();
    return (_jsx("span", { className: `${BASE} ${MATTER_TYPE_COLORS[matterType] ?? DEFAULT_COLOR}`, children: badgeLabel(t, "matterType", matterType) }));
}
export function AssignmentRoleBadge({ role }) {
    const t = useOrgAdminT();
    return (_jsx("span", { className: `${BASE} ${ASSIGNMENT_ROLE_COLORS[role] ?? DEFAULT_COLOR}`, children: badgeLabel(t, "assignmentRole", role) }));
}
// ── Invitation lifecycle badge (invitation-flow Phase 5) ─────────────
//
// NOTE: the invitation-flow plan §U1 prescribed a standalone
// InvitationStatusBadge.tsx file, but every badge in this library lives
// here (StatusBadge / Tier / Role / ClientStatus / MatterStatus /
// MatterType / AssignmentRole). Following the established co-location
// convention over the plan's prescriptive filename keeps the export
// surface + import sites consistent.
const INVITATION_STATUS_COLORS = {
    sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    accepted: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    revoked: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
    expired: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
};
/**
 * Renders the invitation lifecycle state. Pass the value from
 * `effectiveInvitationStatus(user)` (which applies the legacy-user
 * carve-out). `undefined` renders a muted em-dash for users with no
 * invitation at all.
 */
export function InvitationStatusBadge({ status }) {
    const t = useOrgAdminT();
    if (!status) {
        return _jsx("span", { className: "text-xs text-slate-300 dark:text-slate-600", children: "\u2014" });
    }
    return (_jsx("span", { className: `${BASE} ${INVITATION_STATUS_COLORS[status] ?? DEFAULT_COLOR}`, children: badgeLabel(t, "invitation", status) }));
}
//# sourceMappingURL=StatusBadge.js.map